import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Form, Input, Space, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import message from "@/utils/globalMessage";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import ContractCharts from "./ContractCharts";
import {
  CONTRACT_PRIORITIES,
  CONTRACT_STATUS_OPTIONS,
  CONTRACT_STATUS_COLORS,
  CONTRACT_STATUS_LABELS,
  normalizeContractResponse,
} from "./constants";

const EMPTY_FILTERS = {
  title: "",
  priority: [],
  status: [],
};
const EMPTY_FACILITY_IDS = [];
const EMPTY_FACILITIES = [];
const EMPTY_COMPANY_IDS = [];
const EMPTY_COMPANIES = [];

const formatDate = (value) => {
  if (!value) return "--";
  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : "--";
};

const getInitials = (name) => String(name || "?").trim().slice(0, 2).toUpperCase();

export default function ContractList({
  facilityIds = EMPTY_FACILITY_IDS,
  facilities = EMPTY_FACILITIES,
  companyIds = EMPTY_COMPANY_IDS,
  companies = EMPTY_COMPANIES,
  filterByFacility = true,
  onFacilityChange,
  onCompanyChange,
  loadRequests,
  getDetailPath,
  showFacilityColumn = false,
  showRequesterColumn = false,
  emptyText,
  canCreate = false,
  createPath,
}) {
  const navigate = useNavigate();
  const actionRef = useRef();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loadedRows, setLoadedRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const request = useCallback(async (params) => {
    if (filterByFacility && !facilityIds.length) {
      setLoadedRows([]);
      return { data: [], total: 0, success: true };
    }
    try {
      const response = await loadRequests(filterByFacility ? facilityIds : undefined, {
        page: params.current,
        page_size: params.pageSize,
        company: companyIds,
        title: filters.title.trim(),
        priority: filters.priority,
        status: filters.status,
      });
      const { rows, total } = normalizeContractResponse(response);
      setLoadedRows(rows);
      return { data: rows, total, success: true };
    } catch (error) {
      setLoadedRows([]);
      message.error("Failed to load contracts");
      return { data: [], total: 0, success: false };
    }
  }, [companyIds, facilityIds, filterByFacility, filters, loadRequests]);

  // ProTable does not automatically re-run its request callback when the
  // selected facility list or filters change, so explicitly reload it.
  useEffect(() => {
    setLoadedRows([]);
    actionRef.current?.reload?.();
  }, [companyIds, facilityIds, filters]);

  const openDetail = useCallback((record) => {
    navigate(getDetailPath(record));
  }, [getDetailPath, navigate]);

  const columns = useMemo(() => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 220,
      render: (value, record) => (
        <a
          href="#contract-details"
          onClick={(event) => { event.preventDefault(); openDetail(record); }}
          style={{ color: "#1677ff", textDecoration: "underline" }}
        >
          {value}
        </a>
      ),
    },
    {
      title: "Work Order #",
      dataIndex: "workorder_number",
      key: "workorder_number",
      width: 140,
    },
    ...(showFacilityColumn ? [{
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: 180,
    }] : []),
    { title: "Priority", dataIndex: "priority", key: "priority", width: 110 },
    {
      title: "Awarded Contractor",
      dataIndex: "assignTo_name",
      key: "assignTo_name",
      width: 160,
      render: (_, record) => record.assignTo_name ? (
        <Space size={8}>
          <Avatar size={24} src={record.assignTo_logo}>{getInitials(record.assignTo_name)}</Avatar>
          <span>
            <span style={{ display: "block" }}>{record.assignTo_name}</span>
            {record.assignTo_email ? <small style={{ color: "#667085" }}>{record.assignTo_email}</small> : null}
          </span>
        </Space>
      ) : "Unclaimed",
    },
    ...(showRequesterColumn ? [{
      title: "Requested By",
      dataIndex: "createdby_name",
      key: "createdby_name",
      width: 160,
    }] : []),
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 115,
      render: (value) => (
        <Tag color={CONTRACT_STATUS_COLORS[value]}>
          {CONTRACT_STATUS_LABELS[value] || value}
        </Tag>
      ),
    },
    {
      title: "Work Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: 120,
      render: formatDate,
    },
    {
      title: "Created Date",
      dataIndex: "created_date",
      key: "created_date",
      width: 150,
      render: (value) => {
        if (!value) return "--";
        const date = moment(value);
        return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "--";
      },
    },
  ], [openDetail, showFacilityColumn, showRequesterColumn]);

  const updateFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const toggleFilterValue = useCallback((key, value) => {
    if (value === null || value === undefined) return;

    setFilters((current) => {
      const values = current[key] || [];
      const exists = values.some((item) => String(item) === String(value));
      return {
        ...current,
        [key]: exists
          ? values.filter((item) => String(item) !== String(value))
          : [...values, value],
      };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    onFacilityChange?.([]);
  }, [onFacilityChange]);

  const renderSearches = useCallback(() => (
    <Form className="filter-form contract-filter-form" autoComplete="off">
      {companies.length > 0 ? (
        <Form.Item label="Company" name="company">
          <ResponsiveSelect
            mode="multiple"
            allowClear={Boolean(onCompanyChange)}
            showSearch
            disabled={!onCompanyChange}
            popupTitle="Select Companies"
            placeholder="Company"
            value={companyIds}
            options={companies.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onCompanyChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      {filterByFacility && facilities.length > 0 ? (
        <Form.Item label="Facility" name="facility">
          <ResponsiveSelect
            mode="multiple"
            allowClear
            showSearch
            popupTitle="Select Facilities"
            placeholder="Facility"
            value={facilityIds}
            options={facilities.map((item) => ({ value: item.id, label: item.name }))}
            onChange={(value) => onFacilityChange?.(value || [])}
          />
        </Form.Item>
      ) : null}
      <Form.Item label="Title" name="title">
        <Input
          allowClear
          placeholder="Search title"
          value={filters.title}
          onChange={(event) => updateFilter("title", event.target.value)}
        />
      </Form.Item>
      <Form.Item label="Priority" name="priority">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Priorities"
          placeholder="Priority"
          value={filters.priority}
          options={CONTRACT_PRIORITIES.map((value) => ({ value, label: value }))}
          onChange={(value) => updateFilter("priority", value || [])}
        />
      </Form.Item>
      <Form.Item label="Status" name="status">
        <ResponsiveSelect
          mode="multiple"
          allowClear
          popupTitle="Select Statuses"
          placeholder="Status"
          value={filters.status}
          options={CONTRACT_STATUS_OPTIONS}
          onChange={(value) => updateFilter("status", value || [])}
        />
      </Form.Item>
    </Form>
  ), [companies, companyIds, facilities, facilityIds, filterByFacility, filters, onCompanyChange, onFacilityChange, updateFilter]);

  const filterHasValue = Boolean(
    (filterByFacility && facilityIds.length) ||
    filters.title.trim() ||
    filters.priority.length ||
    filters.status.length
  );

  const pageHeader = (
    <Space>
      {canCreate ? (
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(createPath)}>
          New Contract Request
        </Button>
      ) : null}
      <PageHeader
      hide_imports
      hide_search
      hid_new
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={renderSearches}
      resetFilters={resetFilters}
      filterHasValue={filterHasValue}
      />
    </Space>
  );

  return (
    <div style={{ width: "100%", minWidth: 0, padding: 16 }}>
      <ContractCharts
        rows={loadedRows}
        filters={filters}
        onPrioritySelect={(value) => toggleFilterValue("priority", value)}
        onStatusSelect={(value) => toggleFilterValue("status", value)}
      />
      <ResizableProTable
        actionRef={actionRef}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: showFacilityColumn || showRequesterColumn ? 1570 : 1370 }}
        toolBarRender={() => pageHeader}
        locale={{ emptyText: emptyText || (filterByFacility && !facilityIds.length ? "Select one or more facilities" : "No contracts") }}
      />
    </div>
  );
}
